import {
    type FeedingPen,
    type PenWarningTrigger,
    penLabel,
    penWarnings,
    penWarningTier,
    type WarningMetric,
} from '@/domain/feedingDashboard';

import type { PenBarValue } from './PenBarChart';

const SERIES: Record<
    WarningMetric,
    {
        pms: (pen: FeedingPen) => number | null;
        bioscope: (pen: FeedingPen) => number | null;
        pmsTrigger: PenWarningTrigger;
        bioscopeTrigger: PenWarningTrigger;
    }
> = {
    fcr: {
        pms: (pen) => pen.fcrPms,
        bioscope: (pen) => pen.fcrBioscope,
        pmsTrigger: 'fcrSystem',
        bioscopeTrigger: 'fcrBioscope',
    },
    rgi: {
        pms: (pen) => pen.rgiFeed,
        bioscope: (pen) => pen.rgiBioscope,
        pmsTrigger: 'rgiSystem',
        bioscopeTrigger: 'rgiBioscope',
    },
};

/**
 * One bar pair per pen for the bFCR or RGI chart.
 *
 * The tiers come from `penWarnings`, the rule the pen table marks its cells with, rather
 * than from a chart-side comparison: two implementations of "off target" is how the bars
 * once turned alert-red where the table showed a first-tier warning.
 *
 * A pen's Bioscope bar is dropped unless the pen is `ready`, the same mask its table cell
 * applies, so a bar never shows a figure the table withholds.
 *
 * `names` are the period's unique pen labels. The chart keys its bars by `penName`, so two
 * pens sharing a name (one of them historical) would otherwise both draw the second's value.
 */
export function penBarValues(
    pens: FeedingPen[],
    metric: WarningMetric,
    names?: ReadonlyMap<number, string>
): PenBarValue[] {
    const series = SERIES[metric];
    return pens.map((pen) => {
        const warnings = penWarnings(pen);
        return {
            penName: penLabel(names, pen),
            pms: series.pms(pen),
            bioscope: pen.bioscopeDataQuality === 'ready' ? series.bioscope(pen) : null,
            pmsTier: penWarningTier(warnings, series.pmsTrigger),
            bioscopeTier: penWarningTier(warnings, series.bioscopeTrigger),
        };
    });
}
